import { SlackIcon } from '@/components/icons'
import { isSlackExtendedScopesEnabled } from '@/lib/core/config/env-flags'
import { getScopesForService } from '@/lib/oauth/utils'
import type { SubBlockConfig } from '@/blocks/types'
import {
  SLACK_ALL_EVENT_OPTIONS,
  SLACK_SOURCE_OPTIONS,
  SLACK_THREAD_OPTIONS,
  SLACK_TRIGGER_OUTPUTS,
  slackEventsSupportingFilter,
} from '@/triggers/slack/shared'
import type { TriggerConfig } from '@/triggers/types'

// Filter sub-block gating is derived from the catalog's `filters` field so the
// UI conditions and the ingest route share one source of truth.
const SOURCE_FILTER_EVENTS = slackEventsSupportingFilter('source')
const CHANNEL_FILTER_EVENTS = slackEventsSupportingFilter('channels')
const THREAD_FILTER_EVENTS = slackEventsSupportingFilter('threads')
const EMOJI_FILTER_EVENTS = slackEventsSupportingFilter('emoji')
const NAME_FILTER_EVENTS = slackEventsSupportingFilter('name')
const INTERACTION_FILTER_EVENTS = slackEventsSupportingFilter('interaction')
const COMMAND_FILTER_EVENTS = slackEventsSupportingFilter('command')
// Bot/own toggles gate UI visibility only (the route applies them unconditionally),
// so they are not catalog `filters`.
const BOT_FILTER_EVENTS = ['message', 'app_mention']
const OWN_MESSAGE_EVENTS = ['message', 'app_mention', 'reaction_added', 'reaction_removed']
const STREAM_RESPONSE_EVENTS = ['message', 'app_mention', 'assistant_thread_started']
const CUSTOM_BOT_REACTIVE_CONDITION = {
  watchFields: ['customBotCredential'],
  requiredType: 'service_account' as const,
}

/**
 * Unified Slack trigger. A single credential picker lists both the native Ondit
 * Slack app (an OAuth-connected account) and reusable custom bots; the deploy
 * path resolves the credential's kind server-side to pick the backend:
 * - Custom bot: events route by that credential (`webhook.routingKey =
 *   credentialId`) to one shared ingest URL verified with the bot's own signing
 *   secret, so many triggers on the same bot share a single Request URL.
 * - Native Ondit app: events route by Slack `team_id` on the official shared app
 *   (derived at deploy time via `auth.test`, no path or app setup). Only the
 *   events the shared app subscribes to are usable; the deploy path enforces
 *   that (`AGENT_SUBSCRIBED_EVENTS`), so the event picker offers every event
 *   rather than mutating its option set with the selected credential.
 *
 * Native Ondit-app mode is a deployment capability controlled by the existing
 * Slack extended-scopes env pair. Custom bots stay available independently.
 */
export function getSlackTriggerCredentialSubBlock(extendedScopesEnabled: boolean): SubBlockConfig {
  if (!extendedScopesEnabled) {
    return {
      id: 'customBotCredential',
      title: 'Custom Bot',
      type: 'oauth-input',
      canonicalParamId: 'botCredential',
      serviceId: 'slack',
      credentialKind: 'service-account',
      credentialLabels: {
        serviceAccountGroup: 'Custom bots',
        serviceAccountConnect: 'Set up a custom bot',
      },
      requiredScopes: getScopesForService('slack'),
      placeholder: 'Select custom bot',
      description: 'Choose a custom Slack bot you set up once and reuse across triggers.',
      required: true,
      mode: 'trigger',
    }
  }

  return {
    id: 'customBotCredential',
    title: 'Slack Account',
    type: 'oauth-input',
    canonicalParamId: 'botCredential',
    serviceId: 'slack',
    credentialKind: 'any',
    credentialLabels: {
      oauthGroup: 'Ondit app',
      oauthConnect: 'Connect the Ondit app',
      serviceAccountGroup: 'Custom bots',
      serviceAccountConnect: 'Set up a custom bot',
    },
    requiredScopes: getScopesForService('slack'),
    placeholder: 'Select Slack account or bot',
    description:
      'Connect the native Ondit Slack app, or choose a custom Slack bot you set up once and reuse across triggers and actions.',
    required: true,
    mode: 'trigger',
  }
}

export const slackOAuthTrigger: TriggerConfig = {
  id: 'slack_oauth',
  name: 'Slack',
  provider: 'slack_app',
  description: 'Trigger from Slack events, interactions, and slash commands',
  version: '1.0.0',
  icon: SlackIcon,

  subBlocks: [
    {
      id: 'eventType',
      title: 'Event',
      type: 'dropdown',
      options: [...SLACK_ALL_EVENT_OPTIONS],
      placeholder: 'Select an event',
      description:
        'The single Slack event this trigger fires on. Add another trigger block for another event.',
      required: true,
      mode: 'trigger',
    },
    getSlackTriggerCredentialSubBlock(isSlackExtendedScopesEnabled),
    {
      id: 'manualBotCredential',
      title: 'Bot Credential ID',
      type: 'short-input',
      canonicalParamId: 'botCredential',
      placeholder: 'Enter bot credential ID',
      description: 'Set the custom bot credential ID directly.',
      required: true,
      mode: 'trigger-advanced',
    },
    {
      id: 'source',
      title: 'Source',
      type: 'dropdown',
      multiSelect: true,
      options: [...SLACK_SOURCE_OPTIONS],
      placeholder: 'Any source',
      description:
        'Restrict to direct messages, public channels, or private channels. Leave empty to match any.',
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: SOURCE_FILTER_EVENTS },
    },
    {
      id: 'channelFilter',
      title: 'Channels',
      type: 'channel-selector',
      canonicalParamId: 'channelFilter',
      multiSelect: true,
      serviceId: 'slack',
      selectorKey: 'slack.channels',
      placeholder: 'Any channel the bot is in',
      description:
        'Restrict to specific channels. Leave empty to trigger on any channel the bot has been added to.',
      dependsOn: ['customBotCredential'],
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: CHANNEL_FILTER_EVENTS },
    },
    {
      id: 'manualChannelFilter',
      title: 'Channel IDs',
      type: 'short-input',
      canonicalParamId: 'channelFilter',
      placeholder: 'C0123456789, C0987654321',
      description: 'Comma-separated channel IDs to restrict to. Set IDs directly here.',
      required: false,
      mode: 'trigger-advanced',
      condition: { field: 'eventType', value: CHANNEL_FILTER_EVENTS },
    },
    {
      id: 'threads',
      title: 'Threads',
      type: 'dropdown',
      options: [...SLACK_THREAD_OPTIONS],
      value: () => 'include',
      description:
        'Include thread replies, exclude them (top-level only), or fire only on thread replies.',
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: THREAD_FILTER_EVENTS },
    },
    {
      id: 'streamResponse',
      title: 'Enable agent session',
      type: 'switch',
      defaultValue: false,
      description:
        'Create a Slack agent session and stream selected workflow outputs into the conversation that started this run. Custom bots only.',
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: STREAM_RESPONSE_EVENTS },
      reactiveCondition: CUSTOM_BOT_REACTIVE_CONDITION,
    },
    {
      id: 'streamOutputs',
      title: 'Outputs to stream',
      type: 'workflow-output-selector',
      placeholder: 'Select workflow outputs',
      description:
        'Use `<blockName>.<outputPath>` for this workflow or `<childWorkflowId>.<blockName>.<outputPath>` for a child workflow. Selecting a child workflow applies to every invocation of it. Agent outputs stream live; other outputs are sent when the block completes.',
      required: {
        field: 'streamResponse',
        value: true,
        and: { field: 'eventType', value: STREAM_RESPONSE_EVENTS },
      },
      mode: 'trigger',
      condition: {
        field: 'streamResponse',
        value: true,
        and: { field: 'eventType', value: STREAM_RESPONSE_EVENTS },
      },
      reactiveCondition: CUSTOM_BOT_REACTIVE_CONDITION,
    },
    {
      id: 'streamTaskTitle',
      title: 'Response status label',
      type: 'short-input',
      placeholder: 'Running (default)',
      description:
        'Optional status Slack shows while each selected response is being produced. Leave empty to use Running.',
      required: false,
      mode: 'trigger',
      condition: {
        field: 'streamResponse',
        value: true,
        and: { field: 'eventType', value: STREAM_RESPONSE_EVENTS },
      },
      reactiveCondition: CUSTOM_BOT_REACTIVE_CONDITION,
    },
    {
      id: 'streamTaskDisplayMode',
      title: 'Task display',
      type: 'dropdown',
      options: [
        { label: 'Timeline', id: 'timeline' },
        { label: 'Plan', id: 'plan' },
      ],
      value: () => 'timeline',
      description: 'Choose how Slack displays thinking and tool progress.',
      required: false,
      mode: 'trigger',
      condition: {
        field: 'streamResponse',
        value: true,
        and: { field: 'eventType', value: STREAM_RESPONSE_EVENTS },
      },
      reactiveCondition: CUSTOM_BOT_REACTIVE_CONDITION,
    },
    {
      id: 'streamIncludeThinking',
      title: 'Include thinking updates',
      type: 'switch',
      defaultValue: false,
      description: 'Show agent thinking as Slack task updates while the response is generated.',
      required: false,
      mode: 'trigger',
      condition: {
        field: 'streamResponse',
        value: true,
        and: { field: 'eventType', value: STREAM_RESPONSE_EVENTS },
      },
      reactiveCondition: CUSTOM_BOT_REACTIVE_CONDITION,
    },
    {
      id: 'streamIncludeToolCalls',
      title: 'Include tool calls',
      type: 'switch',
      defaultValue: true,
      description: 'Show tool execution lifecycle as Slack task updates.',
      required: false,
      mode: 'trigger',
      condition: {
        field: 'streamResponse',
        value: true,
        and: { field: 'eventType', value: STREAM_RESPONSE_EVENTS },
      },
      reactiveCondition: CUSTOM_BOT_REACTIVE_CONDITION,
    },
    {
      id: 'emoji',
      title: 'Emoji',
      type: 'short-input',
      placeholder: 'thumbsup, white_check_mark',
      description: 'Comma-separated emoji names to restrict to. Leave empty to match any emoji.',
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: EMOJI_FILTER_EVENTS },
    },
    {
      id: 'nameContains',
      title: 'Name contains',
      type: 'short-input',
      placeholder: 'incident-',
      description: 'Only fire when the created channel name contains this text.',
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: NAME_FILTER_EVENTS },
    },
    {
      id: 'interactionFilter',
      title: 'Action / Callback ID',
      type: 'short-input',
      placeholder: 'approve_btn, deny_btn',
      description:
        'Comma-separated action_ids (buttons/selects) or callback_ids (modals) to restrict to. Leave empty to fire on any interaction.',
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: INTERACTION_FILTER_EVENTS },
    },
    {
      id: 'commandFilter',
      title: 'Command',
      type: 'short-input',
      placeholder: '/ask-agent',
      description:
        'Restrict this trigger to one slash command. Leave empty to fire for every command configured on the bot.',
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: COMMAND_FILTER_EVENTS },
    },
    {
      id: 'filterBotMessages',
      title: 'Ignore bot messages',
      type: 'switch',
      defaultValue: true,
      description: "Ignore messages sent by other bots. This app's own output is always ignored.",
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: BOT_FILTER_EVENTS },
    },
    {
      id: 'includeOwnMessages',
      title: 'Include own bot messages',
      type: 'switch',
      defaultValue: false,
      description:
        "Also fire on this app's own messages and reactions. Can cause loops — use with care.",
      required: false,
      mode: 'trigger-advanced',
      condition: { field: 'eventType', value: OWN_MESSAGE_EVENTS },
    },
    {
      id: 'includeFiles',
      title: 'Include file attachments',
      type: 'switch',
      defaultValue: false,
      description: 'Download and include file attachments from messages. Requires files:read.',
      required: false,
      mode: 'trigger',
      condition: { field: 'eventType', value: 'message' },
    },
  ],

  outputs: SLACK_TRIGGER_OUTPUTS,

  webhook: {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  },
}
